import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { VerifyTokenResponse } from '@regatta-replay/types';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

export function AuthVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuthStore();

  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('No verification token provided');
      return;
    }

    async function verify() {
      try {
        const response = await api.get<VerifyTokenResponse>(
          `/auth/verify?token=${encodeURIComponent(token!)}`
        );
        login(response.token, response.user);
        navigate(response.redirectTo || '/admin', { replace: true });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Verification failed'
        );
      }
    }

    verify();
  }, [token, login, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-900 px-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md text-center">
          <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <span className="text-red-600 text-xl">!</span>
          </div>
          <h1 className="text-xl font-semibold text-navy-900 mb-2">
            Verification Failed
          </h1>
          <p className="text-sm text-gray-500 mb-6">{error}</p>
          <Link
            to="/login"
            className="text-brand-ink hover:underline text-sm font-medium"
          >
            Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-900">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md text-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-ink mx-auto mb-4" />
        <p className="text-sm text-gray-500">Verifying your sign-in link...</p>
      </div>
    </div>
  );
}
